import React, { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { logger } from "../../lib/logger";

// =============================================================================
// COHORT RECOVERY CARD — "Late Resolutions" sidecar
// =============================================================================
// Shows the orphan cohort frozen at month-close, then classifies each lead by
// what happened SINCE. Three buckets:
//   - recovered: lead came back, booked, and CLOSED a deal after the orphan
//   - in_flight: lead has a future booking but no close yet
//   - still_orphan: no follow-up activity at all
//
// Why this exists (P0.8): the canonical Show Rate rule (forward-looking SCO
// + future-call_booked exemption) means historical period numbers shimmer
// when a long-silent lead resurfaces. Snapshot-at-close hides the recovery
// signal entirely. This sidecar exposes the recovery as its own visible
// metric — both the live recompute and the cohort vintage stay honest.
// =============================================================================

interface CohortRecoveryStats {
  total_orphans: number;
  recovered: number;
  in_flight: number;
  still_orphan: number;
  recovered_lead_ids: string[];
  in_flight_lead_ids: string[];
}

interface CohortRecoveryCardProps {
  orgId: string;
  repId: string;
  periodStart: string; // YYYY-MM-DD (first of month, e.g. "2026-04-01")
}

const CohortRecoveryCard: React.FC<CohortRecoveryCardProps> = ({ orgId, repId, periodStart }) => {
  // Combined state — single setState path eliminates the eslint
  // "setState-in-effect cascading renders" hit. `stats === undefined` = loading,
  // `stats === null` = errored or empty, object = loaded.
  const [stats, setStats] = useState<CohortRecoveryStats | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    if (!supabase || !orgId || !repId || !periodStart) return;
    supabase
      .rpc("get_cohort_recovery", {
        p_organization_id: orgId,
        p_rep_id: repId,
        p_period_start: periodStart,
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          logger.error("CohortRecoveryCard: get_cohort_recovery failed", error);
          setStats(null);
        } else if (data && data.length > 0) {
          setStats(data[0]);
        } else {
          setStats({
            total_orphans: 0,
            recovered: 0,
            in_flight: 0,
            still_orphan: 0,
            recovered_lead_ids: [],
            in_flight_lead_ids: [],
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, repId, periodStart]);

  const isLoading = stats === undefined;

  if (isLoading) {
    return (
      <div className="mt-3 p-2 rounded-md bg-black/[0.02] border border-black/[0.04]">
        <p className="token-13 text-glass-muted">Loading cohort recovery…</p>
      </div>
    );
  }

  if (!stats || stats.total_orphans === 0) return null;

  const recoveryPct = (stats.recovered / stats.total_orphans) * 100;
  const recoveryColor =
    recoveryPct >= 30 ? "text-ds-green" : recoveryPct >= 10 ? "text-ds-amber" : "text-ds-red";

  return (
    <div className="mt-3 p-3 rounded-md bg-black/[0.02] border border-black/[0.04]">
      <p className="token-11 text-glass-muted uppercase mb-2">
        Cohort recovery — {periodStart.slice(0, 7)}
      </p>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-glass-secondary">
          <span className="token-13">
            <span className="text-glass font-semibold">{stats.total_orphans}</span> orphan
            {stats.total_orphans === 1 ? "" : "s"}
          </span>
          <span className="text-glass-muted">→</span>
          <span className={`token-13 ${recoveryColor} font-semibold`}>
            {stats.recovered} recovered
          </span>
          <span className="token-13 text-glass-secondary">{stats.in_flight} in flight</span>
          <span className="token-13 text-glass-muted">{stats.still_orphan} still orphan</span>
        </div>
        <span className={`token-15 ${recoveryColor} font-semibold`}>{recoveryPct.toFixed(0)}%</span>
      </div>
    </div>
  );
};

export default CohortRecoveryCard;
